/**
 * FlashBook drafts — the list of books this creator saved without publishing.
 *
 * Drafts create no post, so a feed can never lead back to them; this dialog is
 * how the creator finds their unfinished work again and continues editing it.
 *
 * Which is why every row carries the book's own face. A list of titles is a list
 * of *strings*: a creator with two drafts both called "Photosynthesis revision"
 * has to open one to find out which is which, and an unfinished book is
 * recognised by the picture on its cover long before its name is read.
 */
import {useState} from 'react'
import {Image, StyleSheet, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {type MessageDescriptor} from '@lingui/core'
import {msg, plural} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  type FlashBookDraftCover,
  type FlashBookDraftRef,
} from '#/lib/flashbookDrafts'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

export function FlashBookDraftsDialog({
  control,
  drafts,
  openingUri,
  onOpen,
  onRemove,
}: {
  control: Dialog.DialogControlProps
  drafts: FlashBookDraftRef[]
  /** Draft currently being fetched — its row shows a spinner. */
  openingUri: string | null
  onOpen: (draft: FlashBookDraftRef) => void
  onRemove: (draft: FlashBookDraftRef) => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <DialogInner
        drafts={drafts}
        openingUri={openingUri}
        onOpen={onOpen}
        onRemove={onRemove}
      />
    </Dialog.Outer>
  )
}

/** "Saved 2 h ago" without pulling in a date library. */
function savedAgo(iso: string, l: (d: MessageDescriptor) => string) {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (minutes < 1) return l(msg`just now`)
  if (minutes < 60) return l(msg`${minutes} min ago`)
  const hours = Math.round(minutes / 60)
  if (hours < 24) return l(msg`${hours} h ago`)
  return l(msg`${Math.round(hours / 24)} d ago`)
}

/**
 * A draft's cover, at the size of a book on a shelf.
 *
 * Portrait rather than square on purpose: a square tile reads as an avatar, and
 * the thing being recognised here is a *book*. The proportions are a paperback's,
 * not the reader's 9:16 frame, because a row is 46px tall and the frame's shape
 * at that height would be a sliver 26px wide — narrow enough that the artwork
 * stops being recognisable, which is the only reason the tile exists.
 *
 * A cover with no artwork shows the same `book-open` glyph the reader draws when
 * a book has no art, so "nothing here yet" looks the same in both places. A cover
 * that is a *film* shows a film instead: two drafts whose covers are both
 * unpainted are still two different books, and a title below cannot say which one
 * opens on a video.
 */
function CoverTile({cover}: {cover?: FlashBookDraftCover}) {
  const t = useTheme()
  /*
   * The *uri* that failed, not a boolean. A row's own cover cannot change under
   * it in one session, but if a refetch swapped it the artwork has to come back
   * without an effect to notice — comparing uris makes that a property of the
   * value rather than a piece of state to keep in sync.
   */
  const [failedUri, setFailedUri] = useState<string>()
  const artwork = cover?.imageUri
  const showArt = !!artwork && artwork !== failedUri

  return (
    <View
      testID="flashBookDraftCover"
      style={[
        styles.cover,
        t.atoms.bg_contrast_50,
        t.atoms.border_contrast_low,
      ]}>
      {showArt ? (
        <Image
          testID="flashBookDraftCoverArt"
          source={{uri: artwork}}
          style={StyleSheet.absoluteFill}
          resizeMode="cover"
          accessibilityIgnoresInvertColors
          /*
           * Decorative, and deliberately unlabelled: the title beside it already
           * names the book, and a second announcement of the same thing on every
           * row makes the list slower to hear rather than richer.
           */
          accessible={false}
          /*
           * A draft's artwork is a URL that may have been deleted from the PDS
           * since it was saved. Falling back to the glyph keeps the row honest:
           * an empty tile reads as a book with no cover, while a broken image
           * reads as a list that is broken.
           *
           * The fallback is not decoration: React Native Web's own `Image`
           * stops drawing anything once its source fails, so without this the
           * tile would be a blank rectangle in exactly the case it matters.
           */
          onError={() => setFailedUri(artwork)}
        />
      ) : (
        /*
         * The two kinds of unpainted cover are named, not merely drawn.
         *
         * "This one opens on a film" and "this one has nothing on it yet" are
         * different things to tell a creator, and an icon is not readable by
         * anything that needs telling — including the suite that holds the
         * distinction, which otherwise has only a private-use codepoint to go on.
         */
        <View
          testID={
            cover?.isVideo
              ? 'flashBookDraftCoverFilm'
              : 'flashBookDraftCoverNone'
          }>
          <Feather
            name={cover?.isVideo ? 'film' : 'book-open'}
            size={15}
            color={t.atoms.text_contrast_medium.color}
          />
        </View>
      )}
    </View>
  )
}

function DialogInner({
  drafts,
  openingUri,
  onOpen,
  onRemove,
}: {
  drafts: FlashBookDraftRef[]
  openingUri: string | null
  onOpen: (draft: FlashBookDraftRef) => void
  onRemove: (draft: FlashBookDraftRef) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <Dialog.ScrollableInner
      style={web({maxWidth: 420})}
      label={l`Unpublished drafts`}>
      <View style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>
            <Trans>Unpublished drafts</Trans>
          </Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            <Trans>
              Only you can see these. Pick one up to keep editing, or start a
              new book.
            </Trans>
          </Text>
        </View>

        <View style={[a.gap_sm]}>
          {drafts.map(draft => {
            const busy = openingUri === draft.uri
            const pages = l`${plural(draft.pageCount, {
              one: '# page',
              other: '# pages',
            })}`
            const saved = savedAgo(draft.updatedAt, l)
            return (
              <View
                key={draft.uri}
                testID={`flashBookDraftRow--${draft.uri}`}
                style={[
                  a.flex_row,
                  a.align_center,
                  a.gap_sm,
                  t.atoms.bg_contrast_25,
                  t.atoms.border_contrast_low,
                  a.border,
                  a.rounded_sm,
                  a.px_md,
                  a.py_sm,
                ]}>
                <CoverTile cover={draft.cover} />
                <View style={[a.flex_1, a.gap_xs]}>
                  <Text
                    style={[a.text_md, a.font_semi_bold, a.leading_snug]}
                    numberOfLines={1}>
                    {draft.title}
                  </Text>
                  <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                    {pages}
                    {saved ? ` · ${saved}` : ''}
                  </Text>
                </View>
                <Button
                  label={l`Continue editing ${draft.title}`}
                  disabled={busy || !!openingUri}
                  onPress={() => onOpen(draft)}
                  size="small"
                  variant="solid"
                  color="secondary"
                  testID={`flashBookDraftOpen--${draft.uri}`}>
                  <ButtonText>
                    <Trans>Continue</Trans>
                  </ButtonText>
                  {/*
                    The wait, on the row that was tapped.

                    Marked with its own id because it is the thing a browser can
                    see and a reader of the source cannot: that the fetch which
                    follows a tap is *visible* while it is happening, on the row
                    the creator actually pressed.
                  */}
                  {busy ? (
                    <View testID={`flashBookDraftBusy--${draft.uri}`}>
                      <ButtonIcon icon={Loader} />
                    </View>
                  ) : null}
                </Button>
                <Button
                  label={l`Delete draft ${draft.title}`}
                  accessibilityHint={l`Deletes it from your drafts on every device`}
                  disabled={busy || !!openingUri}
                  onPress={() => onRemove(draft)}
                  size="small"
                  variant="ghost"
                  color="secondary"
                  shape="round"
                  testID={`flashBookDraftDelete--${draft.uri}`}>
                  <ButtonIcon icon={X} />
                </Button>
              </View>
            )
          })}
        </View>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}

const styles = StyleSheet.create({
  cover: {
    width: 34,
    height: 46,
    borderRadius: 3,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
